import { Form, Modal } from "antd";
import TextArea from "antd/es/input/TextArea";
import i18n from "i18next";
import { type FC, useCallback, useState } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { useUpdateTableDataMutation } from "../../../../../components";
import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { CsvImportModalButton } from "../../../../../components/FeatureButtons/CsvImportModalButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps, ImportResult } from "../../../../../components/FeatureButtons/types";
import { SelectDynamic } from "../../../../../components/Form/formComponents";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";
import { dataGroupSelectorConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const BatchMoveSiteModal: FC<ButtonProps<SiteEntity>> = ({ isDisabled }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const [form] = Form.useForm();

    const currentDataGroup = getAssignedDataGroup();
    const moveUrl = `${ResourceName.DATA_GROUPS}/${currentDataGroup.code}/${ResourceName.SITES}/${FORM_ACTION.MOVE}`;

    const [batchMoveDataRequest] = useUpdateTableDataMutation();

    const showModal = useCallback(() => setIsModalVisible(true), []);
    const onCancel = useCallback(() => setIsModalVisible(false), []);

    const onOk = useCallback(async () => {
        setUploading(true);

        try {
            const values = await form.validateFields();

            const body = {
                orgUnitIds: values.siteIds,
                dataGroupId: Number(values.destinationDataGroupId),
            };

            batchMoveDataRequest({
                url: moveUrl,
                method: "POST",
                body,
            })
                .unwrap()
                // @ts-ignore
                .then((result) => setImportResult(result))
                .catch((error) => {
                    setImportResult({
                        importErrors: [error?.data?.message || "batchMoveData.modal.importError"],
                    });
                })
                .finally(() => setUploading(false));
        } catch (error) {
            setImportResult({
                importErrors: [typeof error === "string" ? error : "batchMoveData.modal.importError"],
            });
            setUploading(false);
        }
    }, [form, batchMoveDataRequest, moveUrl]);

    const onCsvContent = useCallback(
        (content: string) => {
            if (content) {
                form.setFieldsValue({ siteIds: content });
            }
        },
        [form],
    );

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabled}>
                {i18n.t("button.batchMove")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("batchMoveData.modal.title")}
                onOk={onOk}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton key="import" onClick={onOk} disabled={uploading}>
                        {i18n.t("button.save")}
                    </PrimaryButton>,
                ]}
            >
                <Form form={form}>
                    <ActionBar className="column">
                        <p>{i18n.t("moveData.modal.message")}</p>
                        <Form.Item
                            name="destinationDataGroupId"
                            rules={[{ required: true, message: i18n.t("batchMoveData.error.dataGroupRequired") }]}
                        >
                            <SelectDynamic
                                fieldConfig={dataGroupSelectorConfig}
                                name="destinationDataGroupId"
                                label={i18n.t("moveData.dataGroup")}
                            />
                        </Form.Item>
                        <Form.Item name="siteIds" rules={[{ required: true, message: i18n.t("batchMoveData.error.textAreaRequired") }]}>
                            <TextArea rows={4} placeholder={i18n.t("batchMoveData.modal.textAreaPlaceholder")} name="siteIds" />
                        </Form.Item>
                        <CsvImportModalButton callback={onCsvContent} />
                        {uploading && <ProgressBar />}
                        {importResult && (
                            <RenderImportResult
                                importResult={importResult}
                                showAllErrors={showAllErrors}
                                setShowAllErrors={setShowAllErrors}
                            />
                        )}
                    </ActionBar>
                </Form>
            </Modal>
        </>
    );
};
